<template>
  <article class="block-container">
    <header class="block-head">
      <div>
        <div class="block-title" data-testid="ai-chat-card-title">
          {{ block.title }}
        </div>
        <div
          v-if="block.subtitle"
          class="block-subtitle"
          data-testid="ai-chat-card-subtitle"
        >
          {{ block.subtitle }}
        </div>
      </div>
      <div class="flex items-center block-actions">
        <explain-popover v-if="block.explain" :explain="block.explain" />
        <el-tooltip
          :content="
            isDownloadAvailable
              ? t('aichat.main.blocks.card.download')
              : t('aichat.main.blocks.card.download_unavailable')
          "
          placement="top-end"
          :show-arrow="false"
        >
          <!-- a disabled button fires no mouse events, so the tooltip listens on this wrapper -->
          <span>
            <el-button
              link
              class="block-action-button download-button"
              :icon="DownloadIcon"
              :aria-label="t('aichat.main.blocks.card.download')"
              :disabled="!isDownloadAvailable"
              data-testid="ai-chat-card-download-button"
              @click="emit('download', block)"
            />
          </span>
        </el-tooltip>
      </div>
    </header>
    <section class="block-body">
      <component
        :is="BLOCK_REGISTRY[child.type]"
        v-for="child in children"
        :key="child.id"
        :block="child"
      />
    </section>
  </article>
</template>

<script setup lang="ts">
import { computed, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import { DownloadIcon } from '@shared/assets/icons'
import type { CardBlock } from '../../types'
import BLOCK_REGISTRY from './block-registry'
import { IS_IN_CARD } from './card-context'
import ExplainPopover from './explain-popover.vue'

/* the envelope every data answer sits in: title, the "how this was calculated" popover, the CSV download */
const props = defineProps<{
  block: CardBlock
}>()

const emit = defineEmits<{
  download: [block: CardBlock]
}>()

const { t } = useI18n()

provide(IS_IN_CARD, true)

const isDownloadAvailable = computed(() =>
  Boolean(props.block.download?.available)
)

/* a card without children renders empty; unknown child types are skipped instead of rendering an empty element */
const children = computed(() =>
  (props.block.children ?? []).filter((child) =>
    Object.hasOwn(BLOCK_REGISTRY, child.type)
  )
)
</script>

<style scoped>
.block-container {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--el-border-color-main);
  border-radius: 0.25rem;
  background: var(--el-bg-color);
}

.block-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.block-title {
  color: var(--el-text-color-secondary);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.block-subtitle {
  margin-top: 0.125rem;
  color: var(--el-text-color-secondary);
  font-size: 0.75rem;
}

.block-actions {
  flex-shrink: 0;
  gap: 0.125rem;
}

.block-action-button {
  display: flex;
  align-items: center;
  padding: 0.25rem;
  border: none;
  border-radius: 0.25rem;
  background: none;
  color: var(--el-text-color-secondary);
  cursor: pointer;
}

.block-action-button:hover:not(:disabled) {
  background: var(--el-fill-color);
  color: var(--primary-color);
}

.block-action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.block-action-button svg {
  width: 0.875rem;
  height: 0.875rem;
}

.block-body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* a text child inside a block reads as block copy, not as a chat bubble */
.block-body :deep(.text-block) {
  max-width: none;
  padding: 0;
  background: none;
  font-size: 0.8125rem;
}
</style>
